import React from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Divider,
  Row,
  Col,
  Card,
  Typography,
  Space,
  message,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  BankOutlined,
  HomeOutlined,
  UserOutlined,
  FileTextOutlined,
  MessageOutlined,
  FileOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { sessionsService } from "../../services/api.service";
import logger from "../../utils/logger";

const { Title, Text } = Typography;

const ViewSessionModel = ({
  sessionData,
  visible,
  onClose,
  onDelete,
  onEdit,
}) => {
  // عرض المعلومات في وحدة التحكم للتصحيح
  logger.debug("ViewSessionModel sessionData:", sessionData);

  // تحسين معالجة بيانات الجلسة
  const enhancedSessionData = React.useMemo(() => {
    if (!sessionData) return null;

    const enhanced = { ...sessionData };

    // التأكد من وجود اسم العميل
    if (!enhanced.client_name) {
      enhanced.client_name =
        enhanced.case?.client_name ||
        enhanced.case?.client ||
        enhanced.case?.clients?.[0]?.name ||
        enhanced.clients?.[0]?.name ||
        enhanced.parties?.[0]?.name ||
        "عميل غير محدد";
    }

    // التأكد من وجود نوع الجلسة
    if (!enhanced.session_type) {
      enhanced.session_type =
        enhanced.case?.session_type || enhanced.case?.type || "جلسة قضائية";
    }

    // التأكد من وجود رقم القضية
    if (!enhanced.case_number && enhanced.case?.case_number) {
      enhanced.case_number = enhanced.case.case_number;
    }

    return enhanced;
  }, [sessionData]);

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return "-";
    // استخدام التاريخ الميلادي
    return moment(date).format("YYYY/MM/DD");
  };

  // Helper function to format date in English
  const formatDateInEnglish = (date) => {
    if (!date) return "-";
    // استخدام التاريخ الميلادي بالصيغة DD / MM / YYYY
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day} / ${month} / ${year}`;
  };

  // Define status colors
  const getStatusColor = (status) => {
    switch (status) {
      case "منعقدة":
        return "green";
      case "قادمة":
        return "blue";
      case "مؤجلة":
        return "orange";
      case "ملغية":
        return "red";
      case "مكتملة":
        return "green";
      default:
        return "default";
    }
  };

  // دالة معالجة الحذف
  const handleDelete = () => {
    Modal.confirm({
      title: "تأكيد الحذف",
      content: "هل أنت متأكد من حذف هذه الجلسة؟",
      okText: "نعم",
      cancelText: "لا",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await sessionsService.deleteSession(sessionData.id);
          message.success("تم حذف الجلسة بنجاح");
          onClose();
          if (onDelete) onDelete(sessionData.id);
        } catch (error) {
          message.error("حدث خطأ أثناء حذف الجلسة");
          logger.error("Error deleting session:", error);
        }
      },
    });
  };

  // التأكد من وجود البيانات قبل عرضها
  if (!enhancedSessionData) {
    return (
      <Modal
        title="تفاصيل الجلسة"
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            إغلاق
          </Button>,
        ]}
      >
        <div className="text-center py-8">
          <p>لا توجد بيانات لعرضها</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FileOutlined className="ml-2 text-blue-500" />
            <span className="text-xl font-bold">تفاصيل الجلسة</span>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <div key="footer" className="flex justify-end gap-2">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => onEdit && onEdit(sessionData)}
            size="medium"
          >
            تعديل
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            size="medium"
          >
            حذف
          </Button>
          <Button onClick={onClose} size="medium">
            إغلاق
          </Button>
        </div>,
      ]}
      width={800}
      centered
      destroyOnClose
      className="rtl-modal"
      bodyStyle={{ padding: "20px", maxHeight: "80vh", overflow: "auto" }}
    >
      {sessionData && (
        <div dir="rtl">
          {/* Header Section */}
          <Card
            className="mb-4 shadow-sm"
            style={{
              background: "linear-gradient(to right, #f0f5ff, #e6f7ff)",
            }}
          >
            <Row gutter={16}>
              <Col span={16}>
                <Title level={4} className="m-0">
                  {enhancedSessionData.title || "جلسة بدون عنوان"}
                </Title>
                <Space className="mt-2">
                  <Tag
                    color={getStatusColor(enhancedSessionData.status)}
                    className="text-sm px-3 py-1"
                  >
                    {enhancedSessionData.status || "غير محدد"}
                  </Tag>
                  <Tag color="blue" className="text-sm px-3 py-1">
                    {enhancedSessionData.session_type}
                  </Tag>
                </Space>
              </Col>
              <Col span={8} className="text-left">
                <Text strong>رقم القضية:</Text>
                <Tag color="purple" className="mr-2">
                  {enhancedSessionData.case_number || "-"}
                </Tag>
              </Col>
            </Row>

            {/* عرض اسم العميل */}
            {/* <div className="mt-4">
              <Divider />
              <Text type="secondary">اسم العميل: </Text>
              <Text strong>{enhancedSessionData.client_name}</Text>
            </div> */}
            {/* تمت إزالة التكرار - يتم عرض اسم العميل مرة واحدة فقط في الأعلى */}
            {(() => {
              const clientName =
                sessionData.client_name ||
                sessionData.case?.client_name ||
                sessionData.case?.client ||
                sessionData.case?.clients?.[0]?.name ||
                sessionData.clients?.[0]?.name ||
                sessionData.parties?.[0]?.name;

              if (clientName) {
                return (
                  <div className="mt-4">
                    <Divider />
                    <Text type="secondary">اسم العميل: </Text>
                    <Text strong>{clientName}</Text>
                  </div>
                );
              }
              return null;
            })()}
          </Card>

          {/* Date and Time Section */}
          <Card className="mb-4 shadow-sm">
            <Title level={5} className="mb-3 flex items-center">
              <CalendarOutlined className="ml-2 text-blue-500" />
              معلومات الموعد
            </Title>
            <Row gutter={16}>
              <Col span={12}>
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <Text type="secondary">التاريخ</Text>
                  <div className="flex items-center mt-1">
                    <CalendarOutlined className="ml-2 text-blue-500" />
                    <Text strong>
                      {formatDateInEnglish(
                        enhancedSessionData.date ||
                          enhancedSessionData.session_date
                      )}
                    </Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <Text type="secondary">الوقت</Text>
                  <div className="flex items-center mt-1">
                    <ClockCircleOutlined className="ml-2 text-blue-500" />
                    <Text strong>
                      {enhancedSessionData.time ||
                        enhancedSessionData.session_time ||
                        "-"}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Location Info Section */}
          <Card className="mb-4 shadow-sm">
            <Title level={5} className="mb-3 flex items-center">
              <BankOutlined className="ml-2 text-blue-500" />
              معلومات المكان
            </Title>
            <Row gutter={16}>
              <Col span={12}>
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <Text type="secondary">المحكمة</Text>
                  <div className="flex items-center mt-1">
                    <BankOutlined className="ml-2 text-blue-500" />
                    <Text strong>{enhancedSessionData.court || "-"}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <Text type="secondary">القاعة</Text>
                  <div className="flex items-center mt-1">
                    <HomeOutlined className="ml-2 text-blue-500" />
                    <Text strong>{enhancedSessionData.room || "-"}</Text>
                  </div>
                </div>
              </Col>
              <Col span={24}>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Text type="secondary">القاضي</Text>
                  <div className="flex items-center mt-1">
                    <UserOutlined className="ml-2 text-blue-500" />
                    <Text strong>{enhancedSessionData.judge || "-"}</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Decisions and Notes Section */}
          <Card className="shadow-sm">
            <Title level={5} className="mb-3 flex items-center">
              <FileTextOutlined className="ml-2 text-blue-500" />
              القرارات والملاحظات
            </Title>
            <Row gutter={16}>
              <Col span={24}>
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <Text type="secondary">القرارات</Text>
                  <div
                    className="mt-2 p-3 bg-white border rounded-lg"
                    style={{ whiteSpace: "pre-wrap", minHeight: "60px" }}
                  >
                    {enhancedSessionData.decisions ||
                      enhancedSessionData.case?.last_decision ||
                      "لا توجد قرارات مسجلة"}
                  </div>
                </div>
              </Col>
              <Col span={24}>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Text type="secondary">ملاحظات</Text>
                  <div
                    className="mt-2 p-3 bg-white border rounded-lg"
                    style={{ whiteSpace: "pre-wrap", minHeight: "60px" }}
                  >
                    {enhancedSessionData.notes ||
                      enhancedSessionData.case?.notes ||
                      "لا توجد ملاحظات مسجلة"}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default ViewSessionModel;
